import type { Tone } from '../ui';

/**
 * What an order's state and its waiting time look like.
 *
 * Both used to be class names built from data — `state-${state.toLowerCase()}` and a
 * `waitingClass` that returned `'waiting critical'` — which is the one thing utilities
 * cannot do, because a class that is assembled at runtime is a class the stylesheet never
 * saw. So the mapping is a function returning a token instead, and it is the only place in
 * the console where a value decides an appearance.
 */
export function stateTone(state: string): Tone {
  switch (state) {
    case 'Requested':
      return 'neutral';
    case 'Assigned':
      return 'info';
    case 'EnRouteToPickup':
    case 'OnBoard':
      return 'ok';
    case 'WaitingForRider':
      return 'warn';
    default:
      // Completed, Cancelled and NoShow: finished, and no longer competing for attention.
      return 'neutral';
  }
}

/** Beyond eight minutes somebody is asking where the car is; beyond fifteen they have rung. */
export const WARN_MINUTES = 8;
export const CRITICAL_MINUTES = 15;

/**
 * How urgently a waiting time reads.
 *
 * Weight as well as colour, deliberately: colour alone leaves a colour-blind dispatcher
 * with no signal at all, and this is the column that decides which call gets chased next.
 * A negative figure is a pre-booking that is not yet due — "waiting −480 minutes" would be
 * nonsense — so it reads as quiet rather than as the calmest end of a scale it is not on.
 */
export function waitingLook(minutes: number): string {
  if (minutes < 0) return 'text-ink-3';
  if (minutes >= CRITICAL_MINUTES) return 'text-danger font-bold';
  if (minutes >= WARN_MINUTES) return 'text-warn font-semibold';

  return '';
}
